import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { FilePreview } from "@/components/ui/file-preview";
import { ExpenseStatus, ReportByViewExpense } from "@common/utils/graphql";
import { DataTableColumnHeader } from "@common/components/Table/DataTableColumnHeader";
import { formatDollars, formatDate } from "@common/components/utils";
import { Check, Circle, X } from "@phosphor-icons/react";
import { ExpenseStatusText } from "./employee/ExpenseStatusText";
import { Body } from "@hypercard/web-components";

const defaultIsExpenseOutOfPolicy = () => false;

export const reportsTableColumns = (
  onSelect: (approve: boolean, id: number) => void,
  isReviewed: boolean,
  isReviewMode: boolean,
  isExpenseOutOfPolicy: (
    expense: ReportByViewExpense,
  ) => boolean = defaultIsExpenseOutOfPolicy,
): ColumnDef<ReportByViewExpense>[] => [
  {
    accessorKey: "merchantName",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Merchant" />;
    },
    cell: ({ row }) => {
      if (isExpenseOutOfPolicy(row.original)) {
        return (
          <div className="flex items-center gap-2">
            <Circle weight="fill" className="size-1.5 text-error" />
            <Body size="sm" weight="medium">
              {row.original.merchantName}
            </Body>
          </div>
        );
      }
      return (
        <Body size="md" weight="regular">
          {row.original.merchantName}
        </Body>
      );
    },
  },
  {
    accessorKey: "transactionDate",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Date" />;
    },
    cell: ({ row }) => <div>{formatDate(row.original.transactionDate)}</div>,
  },
  {
    accessorKey: "amount",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Amount" />;
    },
    cell: ({ row }) => <div>{formatDollars(row.original.amount)}</div>,
  },
  {
    accessorKey: "notes",
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="Memo" />;
    },
    cell: ({ row }) => <div>{row.original.notes || "No notes"}</div>,
  },
  {
    accessorKey: "receipt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Receipt" />
    ),
    cell: ({ row }) => {
      return <FilePreview url={row.original.receiptFileUrl} />;
    },
  },
  ...(!isReviewMode
    ? ([
        {
          id: "status",
          enableSorting: false,
          header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Status" />
          ),
          cell: ({ row }) => <ExpenseStatusText expense={row.original} />,
        },
      ] as ColumnDef<ReportByViewExpense>[])
    : []),
  ...(isReviewMode && !isReviewed
    ? ([
        {
          id: "select",
          enableSorting: false,
          header: ({ column }) => (
            <DataTableColumnHeader
              column={column}
              title="Decision"
              className="flex justify-end"
            />
          ),
          cell: ({ row }) => {
            const id = row.original.id;
            const status = row.original.status;
            const isApproved = status === ExpenseStatus.Approved;
            const isRejected = status === ExpenseStatus.Rejected;

            const handleSelect =
              (approve: boolean) =>
              (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onSelect(approve, id);
              };

            return (
              <div className="float-right flex">
                <Button
                  variant={isApproved ? "success" : "secondary"}
                  size="sm"
                  onClick={handleSelect(true)}
                  className="mr-3 w-[100px]"
                >
                  Approve{isApproved ? "d" : ""}
                  <Check
                    className={cn(
                      "ml-2 h-4 w-4",
                      !isApproved && "text-green-600",
                    )}
                  />
                </Button>
                <Button
                  variant={isRejected ? "danger" : "secondary"}
                  size="sm"
                  onClick={handleSelect(false)}
                  className="w-[100px]"
                >
                  Reject{isRejected ? "ed" : ""}
                  <X
                    className={cn(
                      "ml-2 h-4 w-4",
                      !isRejected && "text-rose-600",
                    )}
                  />
                </Button>
              </div>
            );
          },
        },
      ] as ColumnDef<ReportByViewExpense>[])
    : []),
];
